import React, { useState, useEffect } from "react";
import SEO from "../SEO";
import Navigation from "../Navigation";
import Home from "../sections/Home";
import About from "../sections/About";
import Contact from "../sections/Contact";
import PrysmeGroup from "../sections/PrysmeGroup";
import { ChevronUp } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const HomePage = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'contact', 'prysme'];
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
          }
        }
      });

      // Show/hide scroll to top button
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle navigation from legal pages
  useEffect(() => {
    if (location.state?.scrollToSection) {
      const sectionId = location.state.scrollToSection;
      const element = document.getElementById(sectionId);
      if (element) {
        // Small delay to ensure page is loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(sectionId);
        }, 100);
      }
    }
  }, [location.state]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="App">
      <SEO 
        title="SISENS - Restauration Collective d'Excellence Lyon | Cuisine Responsable"
        description="SISENS réinvente la restauration collective à Lyon avec agilité, innovation et écoute. Découvrez SISENS : fier de nos racines lyonnaises, nous allions tradition et modernité pour créer des expériences culinaires responsables, locales et gourmandes. Spécialiste de la restauration d'entreprise, hospitalité et restauration scolaire à Lyon."
        keywords="SISENS, SISENS Lyon, restauration collective Lyon, cuisine responsable, restauration entreprise Lyon, SISENS restauration, cantine entreprise Lyon, restauration scolaire Lyon, restauration collective Rhône-Alpes"
        url="https://sisens.eu"
      />
      <Navigation activeSection={activeSection} setActiveSection={setActiveSection} />
      
      <main>
        <Home />
        <About />
        <Contact />
        <PrysmeGroup />
      </main>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#4b9fcc] text-white p-3 rounded-full shadow-lg hover:bg-[#e71f74] transition-all duration-300 z-40"
          aria-label="Retour en haut"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_sisens-cuisine/artifacts/3lootc7r_25.04.10%20SISENS%20LOGO.png" 
              alt="SISENS Logo" 
              className="h-10 w-auto mx-auto opacity-80"
            />
          </div>
          <p className="text-gray-400 mb-2">
            © Octobre 2025 SISENS. Tous droits réservés. | Restauration
          </p>
          <div className="text-center space-x-4">
            <Link 
              to="/mentions-legales" 
              className="text-gray-400 hover:text-white text-sm transition-colors duration-300 underline"
            >
              Mentions légales
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/politique-confidentialite" 
              className="text-gray-400 hover:text-white text-sm transition-colors duration-300 underline"
            >
              Politique de confidentialité (RGPD)
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/gestion-cookies" 
              className="text-gray-400 hover:text-white text-sm transition-colors duration-300 underline"
            >
              Gestion des cookies
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;